import React, { useRef, useEffect, useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import Peer from 'simple-peer';
import styles from "../healthcare/healthcare.module.scss";
import Images from "../../../../assets/Images";
import JitsiSession from "./JitsiSession";
import { SESSION_TYPES } from "./sessionTypes";
import Popup from "components/minor/popup/popup";
import SessionDock, { DockPanelHeader, DockAccordionItem } from "components/minor/session-dock/session-dock";
import { ClipboardList, HeartPulse, ArrowRight } from "lucide-react";

const useQuery = () => new URLSearchParams(useLocation().search);
function safeSend(ws, data) {
  if (!ws) return;

  if (ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(data));
  } else if (ws.readyState === WebSocket.CONNECTING) {
    console.warn("WS not ready, retrying...");
    setTimeout(() => safeSend(ws, data), 500);
  } else {
    console.error("WS is closed, cannot send", ws.readyState);
  }
}

// Healthcare type — lifted from healthcare/healthcareSession.jsx unchanged,
// except the Jitsi mount now uses the shared JitsiSession wrapper. That also
// fixes a pre-existing bug: the old healthcare/JitsiMeeting.jsx hardcoded
// roomName to the literal "N7" and ignored the prop entirely — JitsiSession
// actually uses the roomName it's given.
const HealthcareBody = () => {
  const transcriptRef = useRef("");
  const navigate = useNavigate();
  const selectedQuestionsRef = useRef([]);
  const query = useQuery();
  const user = query.get('clientId');
   const chatEndRef = useRef(null);
  const localVideo = useRef();
  const remoteVideo = useRef();
  const recognitionRef = useRef(null);
  const [ws, setWs] = useState(null);
  const [organizerWindowOn, setOrganizerWindowOn] = useState(false);
  const [clientId, setClientId] = useState("");
  const [remoteId, setRemoteId] = useState("");
  const [connected, setConnected] = useState(false);
  const [lines, setLines] = useState([]);
  const intervalRef = useRef(null);
  const [openSuccessPopup, setOpenSuccessPopup] = useState(false);
  const [interviewerStatus, setInterviewerStatus] = useState("")

  // Live-session tool dock (replaces the old separate left/right toggle
  // sidebars with the same collapsible icon-dock pattern used across every
  // other live session screen — Meetings, eKYC, Interview).
  const [dockOpen, setDockOpen] = useState(true);
  const [activeTool, setActiveTool] = useState("background");

  // JitsiSession calls this unconditionally on every mouse move over the video
  // area — kept as a required prop even though nothing here reacts to it now
  // that the old toggle-button auto-hide behavior is gone (see SessionDock).
  const handleMouseMoveEvent = () => {};

  const getCurrentTime = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  };
  const handlePopup = () => {
    setOpenSuccessPopup(false)
    setTimeout(() => {
      navigate(`/admin/interviews/list/${interviewerStatus}`);
    }, 1500);
  };
  const handleClosePopup = () => setOpenSuccessPopup(false);
  const mixedStreamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]); // collect audio chunks
  const getMixedStream = () => {
    const localStream = localVideo.current?.srcObject;
    const remoteStream = remoteVideo.current?.srcObject;

    if (!localStream && !remoteStream) return null;

    const context = new AudioContext();
    const destination = context.createMediaStreamDestination();

    if (localStream) {
      const source1 = context.createMediaStreamSource(localStream);
      source1.connect(destination);
    }

    if (remoteStream) {
      const source2 = context.createMediaStreamSource(remoteStream);
      source2.connect(destination);
    }

    const tracks = [];
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) tracks.push(videoTrack);
    }

    tracks.push(...destination.stream.getAudioTracks());

    return new MediaStream(tracks);
  };

  useEffect(() => {

    const params = new URLSearchParams(window.location.search);
    const clientFromURL = params.get("clientId");
    const roomFromURL = params.get("roomId");

    if (clientFromURL) {
      setClientId(clientFromURL);
    }
    if (roomFromURL) {
      setRemoteId(roomFromURL);
    }

    if (!clientId || connected) return;

    const wsClient = new WebSocket(`ws://localhost:8000/ws/${clientId}`);

    wsClient.onopen = () => {
  console.log("✅ WebSocket connected");
    wsClient.send(JSON.stringify({ type: "hello", id: clientId }));
    setWs(wsClient);
  };

    navigator.mediaDevices.getUserMedia({
      video: true, audio: true
    }).then(stream => {
      localVideo.current.srcObject = stream;
      const newPeer = new Peer({
        initiator: true,
        trickle: false,
        stream,
      });

      newPeer.on("signal", signal => {
        safeSend(ws, { type: "signal", to: remoteId });
      });

      wsClient.onmessage = (msg) => {
        const data = JSON.parse(msg.data);
        if (data.type === "signal") {
          newPeer.signal(data.signal);
        } else if (data.type === "chat") {
          setMessages(prev => [...prev, { sender: "peer", text: data.text }]);
        }
        else if (data.type === "transcript") {
          setLines(prev => [...prev, data.line]);
        }
      };
      newPeer.on("stream", remoteStream => {
        remoteVideo.current.srcObject = remoteStream;
      });
    //   setPeer(newPeer);
      setConnected(true);

    });
  }, [clientId, remoteId, connected]);

  useEffect(() => {
    const intervalId = setInterval(() => { }, 2000);
    intervalRef.current = intervalId;
    return () => {
      clearInterval(intervalRef.current);
    };
  }, []);


 const startListening = (speaker) => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { alert("Speech recognition not supported"); return; }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => { setIsListening(false); recognition.start(); };

    recognition.onresult = (event) => {
      let interimTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        interimTranscript += event.results[i][0].transcript + " ";
      }

      for (let i = event.resultIndex; i < event.results.length; i++) {

        if (event.results[i].isFinal) {
            const senten = event.results[i][0].transcript.trim();
            const entry = { by: speaker, text: senten, time: getCurrentTime() };
            if (senten) {
              setLines((prev) => [...prev, entry]);
              transcriptRef.current = senten; // ⬅️ Update latest final result
          }
        }

          if (ws) {
        ws.send(JSON.stringify({
          type: "transcript",
          to: remoteId,
          line: entry,
        }));
      }
      }
    };
    recognition.onerror = (e) => console.error("Speech error:", e.error);
    recognition.start();
    recognitionRef.current = recognition;
  };



useEffect(() => {
  if (organizerWindowOn) {
    if (user === "organizer") startListening("organizer");
    else if (user === "attendees") startListening("attendees");
  }
}, [organizerWindowOn, user]);
  if (!clientId || !remoteId) {
    return (
      <div className="h-screen w-screen bg-gray-950 text-white flex flex-col items-center justify-center gap-4 p-6">
        <h1 className="text-2xl font-bold mb-4">Enter your details to start a call</h1>
        <input
          type="text"
          placeholder="Your ID"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          className="p-2 rounded-md bg-gray-800 w-full max-w-sm focus:outline-none"
        />
        <input
          type="text"
          placeholder="Remote ID to connect"
          value={remoteId}
          onChange={(e) => setRemoteId(e.target.value)}
          className="p-2 rounded-md bg-gray-800 w-full max-w-sm focus:outline-none"
        />
        <button
          onClick={() => setConnected(false)} // Triggers useEffect
          className="mt-2 bg-blue-600 hover:bg-blue-500 px-6 py-2 rounded-md"
        >
          Connect
        </button>
      </div>
    );
  }

  const handleChildClick = () => {
    setOrganizerWindowOn(true)
  };

  const jitsi = SESSION_TYPES.healthcare.jitsi;

  return (
    <div className={styles.appContainer}>
      <div className={`${styles.videoControlWrapper} flex-1 relative flex flex-col items-center justify-center px-6 py-8`}>
        <div className='w-full h-screen'>
          <video
            style={{ display: 'none' }}
            ref={localVideo}
            muted
            autoPlay
            playsInline
            className='w-full h-full object-cover'
          />
        </div>
      </div>

      <div className={styles.videoContainer}>
        <JitsiSession
          roomName={remoteId}
          scriptSrc={jitsi.scriptSrc}
          configOverwrite={jitsi.configOverwrite}
          interfaceConfigOverwrite={jitsi.interfaceConfigOverwrite}
          onButtonClick={handleChildClick}
          mouseEventTriggered={handleMouseMoveEvent}
          chatOpen={dockOpen}
          onLeave={() => navigate("/meetingended")}
        />

        <Popup open={openSuccessPopup}
          message="Your request has been submitted"
          title="Thank You"
          handleClose={handleClosePopup}
          handleConfirm={() => handlePopup()} type="success" />

        {/* ── Live-session tool dock — Patient Background (history/context
             gathered before the call) and Live AI Vitals (read during the
             call). Same collapsible icon-dock pattern as every other live
             session screen. ── */}
        {organizerWindowOn && (
          <SessionDock
            open={dockOpen}
            onToggleOpen={setDockOpen}
            activeTool={activeTool}
            onSelectTool={(key) => setActiveTool((prev) => (prev === key ? null : key))}
            collapsedIcon={HeartPulse}
            drawerWidth={340}
            tools={[
              { key: "background", icon: ClipboardList, label: "Patient Background" },
              { key: "vitals",     icon: HeartPulse,     label: "Live AI Vitals" },
            ]}
          >
            {activeTool === "background" && (
              <>
                <DockPanelHeader icon={ClipboardList} title="Patient Background" onClose={() => setActiveTool(null)} />
                <div className="sdock-scroll" style={{ flex: 1, overflowY: "auto", padding: "4px 18px" }}>
                  <DockAccordionItem title="Current Health Issues" defaultOpen>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {[
                        { name: "Headache", duration: "2 days", symptoms: "Nausea, fatigue", severity: "low" },
                        { name: "Frequent Urination", duration: "1 month", symptoms: "Fatigue, Weight loss", severity: "high" },
                        { name: "Sneezing", duration: "1 week", symptoms: "Stuffy nose, Sore throat", severity: "high" },
                      ].map((issue, i) => (
                        <div key={i} className="sdock-line" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "9px 12px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: 12.5, fontWeight: 700, color: "#E8F0FE" }}>{issue.name}</span>
                            <span style={{ fontSize: 10, color: "#7A93B4" }}>{issue.duration}</span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
                            <span style={{ fontSize: 11.5, color: "#C8D6E8" }}>Symptoms: {issue.symptoms}</span>
                            <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                              color: issue.severity === "high" ? "#FF4D6D" : "#00E5A3",
                              background: issue.severity === "high" ? "rgba(255,77,109,0.14)" : "rgba(0,229,163,0.14)" }}>
                              {issue.severity === "high" ? "High" : "Low"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </DockAccordionItem>

                  <DockAccordionItem title="Medical History">
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <div><div style={{ fontSize: 10.5, color: "#7A93B4", textTransform: "uppercase", letterSpacing: "0.04em" }}>Procedures & Surgeries</div>
                        <div style={{ fontSize: 12, color: "#E8F0FE", marginTop: 3 }}>Coronary artery bypass grafting, angioplasty</div></div>
                      <div><div style={{ fontSize: 10.5, color: "#7A93B4", textTransform: "uppercase", letterSpacing: "0.04em" }}>Allergies</div>
                        <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                          {["Penicillin", "Atopic Eczema"].map((a) => (
                            <span key={a} style={{ fontSize: 10.5, padding: "2px 9px", borderRadius: 20, background: "rgba(255,184,0,0.14)", color: "#FFB800", fontWeight: 600 }}>{a}</span>
                          ))}
                        </div></div>
                      <div><div style={{ fontSize: 10.5, color: "#7A93B4", textTransform: "uppercase", letterSpacing: "0.04em" }}>Medications</div>
                        <div style={{ fontSize: 12, color: "#E8F0FE", marginTop: 3 }}>Aspirin, Beta-Blockers</div></div>
                      <div><div style={{ fontSize: 10.5, color: "#7A93B4", textTransform: "uppercase", letterSpacing: "0.04em" }}>Vaccination History</div>
                        <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                          {["Covid-19", "Hepatitis"].map((v) => (
                            <span key={v} style={{ fontSize: 10.5, padding: "2px 9px", borderRadius: 20, background: "rgba(0,200,255,0.14)", color: "#00C8FF", fontWeight: 600 }}>{v}</span>
                          ))}
                        </div></div>
                    </div>
                  </DockAccordionItem>

                  <DockAccordionItem title="Previous Lab Reports">
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="sdock-line" style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                          background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "9px 12px" }}>
                          <div>
                            <div style={{ fontSize: 10, color: "#7A93B4" }}>Uploaded on 06-Oct-2025</div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#E8F0FE", margin: "2px 0" }}>Master Full body Health Checkup…</div>
                            <div style={{ fontSize: 11, color: "#7A93B4" }}>Vijaya Diagnostics</div>
                          </div>
                          <ArrowRight size={14} color="#3D5473" />
                        </div>
                      ))}
                    </div>
                  </DockAccordionItem>

                  <DockAccordionItem title="Family History">
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {[
                        { condition: "Cardiovascular diseases", relative: "Mother", age: "50+" },
                        { condition: "Heart Disease", relative: "Father", age: "60+" },
                      ].map((f, i) => (
                        <div key={i} className="sdock-line" style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#E8F0FE" }}>
                          <span>{f.condition}</span>
                          <span style={{ color: "#7A93B4" }}>{f.relative} · {f.age}</span>
                        </div>
                      ))}
                    </div>
                  </DockAccordionItem>

                  <DockAccordionItem title="Lifestyle">
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      {[
                        { label: "Occupation", value: "Software Engineer" },
                        { label: "Marital Status", value: "Married" },
                        { label: "Tobacco Use", value: "Never" },
                        { label: "Alcohol Use", value: "Occasional" },
                        { label: "Recreational Drug Use", value: "None" },
                        { label: "Diet & Exercise", value: "Vegetarian, walks 30 mins" },
                        { label: "Sleep Pattern", value: "8 hrs / night" },
                      ].map((item) => (
                        <div key={item.label}>
                          <div style={{ fontSize: 10, color: "#7A93B4" }}>{item.label}</div>
                          <div style={{ fontSize: 11.5, color: "#E8F0FE", marginTop: 2 }}>{item.value}</div>
                        </div>
                      ))}
                    </div>
                  </DockAccordionItem>
                </div>
              </>
            )}

            {activeTool === "vitals" && (
              <>
                <DockPanelHeader icon={HeartPulse} title="Live AI Vitals" onClose={() => setActiveTool(null)} />
                <div className="sdock-scroll" style={{ flex: 1, overflowY: "auto", padding: "4px 18px" }}>
                  <DockAccordionItem title="Vitals via Video (rPPG)" defaultOpen>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {[
                        { icon: Images.HEART_BEAT, label: "Heart Rate", value: "180 beats / min" },
                        { icon: Images.LUNGS, label: "Respiratory Rate", value: "180 beats / min" },
                        { icon: Images.STRESS, label: "Stress Indicators", value: "High" },
                      ].map((v) => (
                        <div key={v.label} className="sdock-line" style={{ display: "flex", alignItems: "center", gap: 10,
                          background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "9px 12px" }}>
                          <img src={v.icon} alt={v.label} width={22} />
                          <div>
                            <div style={{ fontSize: 10.5, color: "#7A93B4" }}>{v.label}</div>
                            <div style={{ fontSize: 12.5, fontWeight: 700, color: "#E8F0FE" }}>{v.value}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </DockAccordionItem>

                  {["Facial & Skin Analysis", "Voice/Speech Analysis", "Pain/Emotion Detection", "Movement & Motor Skills", "Cognitive & Mental Health"].map((title) => (
                    <DockAccordionItem key={title} title={title}>
                      <div style={{ fontSize: 11.5, color: "#3D5473", fontStyle: "italic" }}>Not yet available for this session.</div>
                    </DockAccordionItem>
                  ))}
                </div>
              </>
            )}
          </SessionDock>
        )}
      </div>
    </div>
  );
};

export default HealthcareBody;
